import { AlertMessageLink } from '@/app/components'
import { ID } from '@/types/model'
import { useDeviceEventExplanation } from './useEventExplanation'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { changeVehicleChargingDelayEvent } from '@/actions/vehicleChargingDelayEvents'
import { chargeNowEventCollection } from '@/reducers/chargeNowEvents'
import { chargeForecastCollection } from '@/reducers/chargeRelatedCollections'
import { useState } from 'react'
import VehicleDREventOptInConfirmationModal from './VehicleDREventOptInConfirmationModal'
import DeviceOptedOutModal from './DeviceOptedOutModal'
import DROptOutModal from '@/authenticated/components/ChargeForecastDashboardView/SmartChargeSchedule/DROptOutModal'
import { DeviceType } from '@/app/components/DeviceSelectionGallery/DeviceSelectionGallery.types'
import { selectNextDeviceDREvent } from '@/selectors/vehicleDemandResponseEvent/selectNextVehicleDREvent'
import { vehicleDemandResponseEventsCollection } from '@/reducers/vehicleDemandResponseEvents'
import { VEHICLE_DR_EVENT_HELP_URL } from './drEventHelpUrls'

type Props = {
  deviceId: ID
  deviceType: DeviceType
  showAlertMessageLink?: boolean
}

export default function DeviceDREventAlertMessage(props: Props) {
  const { deviceId, deviceType, showAlertMessageLink = true } = props
  const dispatch = useAppDispatch()
  const [isOptInConfirmationModalOpen, setOptInConfirmationModalOpen] =
    useState(false)
  const [isOptOutConfirmationModalOpen, setOptOutConfirmationModalOpen] =
    useState(false)
  const [isManageEventModalOpen, setManageEventModalOpen] = useState(false)

  // Fetch delay events using the collection
  vehicleDemandResponseEventsCollection.useFetch({
    params:
      deviceType === 'vehicle'
        ? { vehicle_id: deviceId }
        : { charger_id: deviceId },
  })

  const nextEvent = useAppSelector((state) =>
    selectNextDeviceDREvent(state, deviceId, deviceType),
  )
  const message = useDeviceEventExplanation(deviceId, deviceType)

  const eventId = nextEvent?.id

  const invalidateRelatedData = () => {
    dispatch(chargeNowEventCollection.actions.invalidate())
    dispatch(chargeForecastCollection.actions.invalidate())
    dispatch(vehicleDemandResponseEventsCollection.actions.invalidate())
  }

  const handleOptIn = () => {
    if (!eventId) return
    dispatch(
      changeVehicleChargingDelayEvent(eventId, {
        enrolled: true,
        reason: 'opt-in',
      }),
    )
      .then(invalidateRelatedData)
      .then(() => setOptInConfirmationModalOpen(true))
  }

  const handleManageClick = () => {
    setManageEventModalOpen(true)
  }

  const handleOptedOut = () => {
    setOptOutConfirmationModalOpen(true)
  }

  if (!showAlertMessageLink || !nextEvent) {
    return null
  }

  const isOptedIn = nextEvent.enrolled
  const linkText = isOptedIn ? 'Manage' : 'Opt In'
  const onLinkClick = isOptedIn ? handleManageClick : handleOptIn

  return (
    <div>
      <AlertMessageLink
        variant="info"
        onLinkClick={onLinkClick}
        linkText={linkText}
      >
        {message}
      </AlertMessageLink>
      {nextEvent && (
        <DROptOutModal
          open={isManageEventModalOpen}
          onClose={() => setManageEventModalOpen(false)}
          drEvent={nextEvent}
          deviceType={deviceType}
          onOptedOut={handleOptedOut}
          helpLinkUrl={
            deviceType === 'vehicle' ? VEHICLE_DR_EVENT_HELP_URL : null
          }
        />
      )}
      <VehicleDREventOptInConfirmationModal
        open={isOptInConfirmationModalOpen}
        onClose={() => setOptInConfirmationModalOpen(false)}
        deviceId={deviceId}
        deviceType={deviceType}
      />
      <DeviceOptedOutModal
        open={isOptOutConfirmationModalOpen}
        onClose={() => setOptOutConfirmationModalOpen(false)}
      />
    </div>
  )
}
